import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import PropTypes from "prop-types"
import moment from "moment"
import classNames from "classnames"
import { Badge, Divider, IconButton, Paper } from "@mui/material"
import { FilterList, ResetTvOutlined } from "@mui/icons-material"
import { isEmpty, isNil, some } from "lodash"
import { Creators as DialogCreators } from "modules/dialog/model"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { getAiTermsAccepted } from "modules/account-root/selectors"
import CompareDateSelector from "connected-components/compare/components/date-selector"
import ListItemDropDown from "components/list-item-dropdown"
import OPTooltip from "components/material/tooltip"
import { CONTENT_SET_METADATA } from "resources/content-sets"
import { useIsDashboardOptimizeExperimentsFeatureEnabled, useIsAiChatFeatureEnabled, useAccountAlias, useAccountId, useUserId } from "resources/account"
import { getIsIntradayAnalyticsEnabled } from "selectors/features"
import { OK, ERROR, DATA_STATUSES } from "helpers/data-statuses"
import { CUSTOM_PERIOD, SAME_DAY, TIME_FORMAT_TZ } from "helpers/dates"
import { createReduceDownloadData } from "./helpers/download-aggregator"
import { downloadRegistry } from "./context/download-registry"
import useIsConstituentsDownloadAllowed from "hooks/use-is-constituents-download-allowed"
import { getDate } from "modules/intraday/selectors/dates"
import { getOrCreateIntradaySocket } from "socket/intraday-connection"
import { useAutoRunOptimization } from "connected-components/optimize-experiments/hooks/use-auto-run-optimization"
import { MODEL_METADATA_SHAPE, INSTRUMENT_METADATA_SHAPE } from "connected-components/asset-view/prop-types"
import QuickAnswersWrapper from "connected-components/quick-answers"
import OptimizeExperimentsPanel from "connected-components/optimize-experiments/components/optimize-experiments-panel"
import { DashboardContext } from "./context/dashboard"
import { ModulesProvider } from "./context/modules"
import DashboardModulesRenderer from "./components/dashboard-modules-renderer"
import DashboardDownload from "./components/download"
import FilterSelector from "./components/filter-selector"
import TradeManager from "./components/trader-manager"
import { getModeSelectorOptions } from "./helpers/mode-selector"
import { getDownloadDataFileName } from "./helpers/download"
import { getIndexPositionSets } from "./helpers/positions"
import { useAnalytics } from "connected-components/analytics"
import { DASHBOARD_EVENTS, useDashboardLoadedTracking, useModeChangeTracking, useModelChangeTracking } from "./analytics"
import { useAdjustedDashboardDates } from "./hooks/dates"
import { useIntradayWarningNotification } from "./hooks/use-intraday-warning-notification"
import { useIntradayRefresh } from "./hooks/use-intraday-refresh"
import useMixPanel from "hooks/use-mixpanel"
import { useIsMixpanelSessionReplayEnabled } from "resources/account"
import IntradayDataInfo from "./components/intraday-data-info"
import LatestPriceDisplay from "./components/latest-price-display"
import DashboardTopBar from "./components/top-bar"
import { RULES } from "connected-components/compare/position-sets"
import { usePinArtifactHandler } from "./hooks/use-pin-artifact-handler"
import { useRemoveArtifactHandler } from "./hooks/use-remove-artifact-handler"

import "./style.scss"

const PERIOD_SELECTOR_LABELS = {
  from: "Start",
  to: "End (As of)",
}

export default function Dashboard(props) {
  const {
    instrument: original,
    setInstrument, filter,
    secondInstrument: rebalanced,
    comparisonInstrument: comparison,
    setComparisonInstrument,
    selectedMode,
    setSelectedMode,
    period,
    startDate,
    endDate,
    setPeriod,
    setBoundaryDates,
    useLatestDate,
    setUseLatestDate,
    setPerformanceStartDate,
    setPerformanceEndDate,
    isIntraday,
    isEndDateIntraday,
    intradayBatchMetadata,
    setIntradayBatchMetadata,
    isEndVsStartDateComparison,
    setIsEndDateIntraday,
    isPreferencesLoaded,
    setOptimizeConfiguration,
    optimizeConfiguration,
    modelId,
    setModelId,
    base,
    isOptimizePanelExpanded,
    setIsOptimizePanelExpanded,
    hasUserAppliedOptimization,
  } = useContext(DashboardContext)
  const { modelMetadata, instrumentsWithMetadata, contentSets, hasMultipleInstruments, instrumentMetadata, comparisonInstrumentMetadata, baseInstrumentMetadata, lastIntradayBatch, lastIntradayBatchStatus, TradeContext, tradeContextValue } = props

  const analytics = useAnalytics()
  const accountId = useAccountId()
  const accountAlias = useAccountAlias()
  const userId = useUserId()
  const isMixpanelSessionReplayEnabled = useIsMixpanelSessionReplayEnabled()

  const mixpanelEventProperties = useMemo(() => ({
    source: "dashboard",
    account_id: accountId,
    account_alias: accountAlias,
    instrument_type: original?.type,
    instrument_id: original?.id,
  }), [accountId, accountAlias, original?.type, original?.id])

  useMixPanel({
    distinctId: isMixpanelSessionReplayEnabled ? userId : null,
    eventProperties: mixpanelEventProperties,
  })
  useDashboardLoadedTracking()
  useModeChangeTracking(selectedMode)
  useModelChangeTracking(modelId)

  const { resetTrades, trades } = tradeContextValue

  const [filterButtonAnchor, setFilterButtonAnchor] = useState(null)
  const [filterSelectorStatus, setFilterSelectorStatus] = useState(false)

  const dockviewLayoutRef = useRef(null)

  const isAiTermsAccepted = useSelector(getAiTermsAccepted)
  const isAiChatFeatureEnabled = useIsAiChatFeatureEnabled()

  const dispatch = useDispatch()
  const [layoutActions, setLayoutActions] = useState(null)
  const reduceDownloadData = useMemo(() =>
    createReduceDownloadData(() => layoutActions?.getPanelOrder?.() ?? []),
  [layoutActions],
  )

  useEffect(() => {
    return () => downloadRegistry.clear()
  }, [])
  const intradayDataAvailable = !isEmpty(lastIntradayBatch) && lastIntradayBatchStatus === OK

  useIntradayWarningNotification()

  const isOptimizeExperimentsEnabled = useIsDashboardOptimizeExperimentsFeatureEnabled()
  const isIntradayAnalyticsEnabled = useSelector(getIsIntradayAnalyticsEnabled)
  useAutoRunOptimization(original, modelId, endDate, base, setOptimizeConfiguration, optimizeConfiguration, setComparisonInstrument, isOptimizeExperimentsEnabled, isOptimizePanelExpanded, hasUserAppliedOptimization)

  const currentAsOf = useMemo(() => {
    if (!intradayBatchMetadata?.asOf) {
      return "-"
    }
    return moment(intradayBatchMetadata.asOf, TIME_FORMAT_TZ).format("LTS")
  }, [intradayBatchMetadata])

  const isIntradayInstrument = !!instrumentsWithMetadata?.original?.intradayEnabled

  const latestPriceTimestamp = useMemo(() => {
    if (!isIntradayAnalyticsEnabled || isIntradayInstrument || !isEndDateIntraday || !intradayBatchMetadata?.asOf) return null
    return moment(intradayBatchMetadata.asOf).format("LTS")
  }, [isIntradayAnalyticsEnabled, isIntradayInstrument, isEndDateIntraday, intradayBatchMetadata])

  useEffect(() => {
    if (!isIntradayAnalyticsEnabled || isIntradayInstrument || !isEndDateIntraday) return
    const socket = getOrCreateIntradaySocket(accountId)

    function handleTimestampUpdate({ timestamp }) {
      setIntradayBatchMetadata({ asOf: timestamp })
    }

    socket.on("timestampUpdated", handleTimestampUpdate)
    return () => {
      socket.off("timestampUpdated", handleTimestampUpdate)
    }
  }, [isIntradayAnalyticsEnabled, isIntradayInstrument, isEndDateIntraday, accountId, setIntradayBatchMetadata])

  const intradayDate = getDate()

  useEffect(() => {
    setIsEndDateIntraday(endDate === intradayDate)
  }, [endDate, intradayDate, setIsEndDateIntraday])

  const modeSelectorOptions = useMemo(() =>
    getModeSelectorOptions(rebalanced, comparison, isEndVsStartDateComparison),
  [rebalanced, comparison, isEndVsStartDateComparison])

  const handleFiltersToggle = useCallback((event) => {
    if (filterButtonAnchor) {
      analytics.track(DASHBOARD_EVENTS.FILTERS_CLOSED)
      setFilterButtonAnchor(null)
    } else {
      analytics.track(DASHBOARD_EVENTS.FILTERS_OPENED)
      setFilterButtonAnchor(event.currentTarget)
    }
  }, [filterButtonAnchor, setFilterButtonAnchor, analytics])

  const { adjustedStartDate, earliestDate, latestDate, unmodeledDates, datesAdjusted, isLoading: isLoadingDates } = useAdjustedDashboardDates({
    modelMetadata,
    instrumentsWithMetadata,
    period,
    startDate,
    endDate,
    useLatestDate,
    setUseLatestDate,
    setPeriod,
    setBoundaryDates,
    setPerformanceStartDate,
    setPerformanceEndDate,
    trackPeriodShortcutDateChanged: (oldStartDate, newStartDate, newEndDate, period) => analytics.track(DASHBOARD_EVENTS.DATE_SHORTCUT_CHANGED, {
      oldStartDate,
      newStartDate,
      newEndDate,
      period,
    }),
    instrument: original,
    isIntraday,
    intradayDataAvailable,
    isIntradayAnalyticsEnabled,
  })

  const filtersCount = useMemo(() => {
    if (!filter) {
      return 0
    }
    return Object.keys(filter).length
  }, [filter])

  const isFiltersBadgeVisible = !filterButtonAnchor && filtersCount > 0

  useEffect(() => {
    if (instrumentMetadata?.unavailable) {
      setInstrument(null)
      dispatch(NotificationCreators.add("warning", null, "The main instrument could not be found and was reset."))
    } else if (comparisonInstrumentMetadata?.unavailable) {
      setComparisonInstrument(null)
      dispatch(NotificationCreators.add("warning", null, "The comparison instrument could not be found and was reset."))
    }
  }, [instrumentMetadata, setInstrument, setComparisonInstrument, comparisonInstrumentMetadata, dispatch])

  useEffect(() => {
    if (!isIntraday || !lastIntradayBatch || trades?.length > 0) {
      return
    }

    // Do not auto refresh if user is looking at historical data in intraday mode
    if (isIntraday && intradayDataAvailable && !isEndDateIntraday) {
      return
    }

    if (lastIntradayBatch.asOf !== intradayBatchMetadata?.asOf) {
      setIntradayBatchMetadata({
        asOf: lastIntradayBatch.asOf,
        id: lastIntradayBatch.id,
      })
    }
  }, [isIntraday, lastIntradayBatch, setIntradayBatchMetadata, intradayBatchMetadata, trades, intradayDataAvailable, isEndDateIntraday])

  useEffect(() => {
    if (isEndVsStartDateComparison && original && comparison?.id !== original.id) {
      setComparisonInstrument(original)
    } else if (comparison?.type === RULES && comparison.rulesConfiguration.source.positionSet.id !== original.id) {
      setComparisonInstrument({
        type: RULES,
        rulesConfiguration: {
          source: { positionSet: { id: original.id, type: original.type } },
          rules: [...comparison.rulesConfiguration.rules],
        },
      })
    }
  }, [isEndVsStartDateComparison, setComparisonInstrument, original, comparison])

  useEffect(() => {
    if (!modeSelectorOptions || !selectedMode) {
      return
    }

    const isSelectedModeAvailable = some(modeSelectorOptions, option => option.id === selectedMode && !option.disabled)
    if (!isSelectedModeAvailable) {
      const newMode = modeSelectorOptions.find(option => !option.disabled)
      if (newMode) {
        setSelectedMode(newMode.id)
      }
    }
  }, [modeSelectorOptions, selectedMode, setSelectedMode])

  const setPeriodWithChange = useCallback((change) => {
    if (typeof change === "string") {
      setPeriod(change)
    } else {
      const newStartDate = change.adjusted?.startDate || change.startDate
      const newEndDate = change.adjusted?.endDate || change.endDate
      setPeriod({
        period: CUSTOM_PERIOD,
        startDate: newStartDate,
        endDate: newEndDate,
      })

      setUseLatestDate(newEndDate === intradayDate)
      analytics.track(DASHBOARD_EVENTS.CUSTOM_DATES_CHANGED, {
        oldStartDate: startDate,
        oldEndDate: endDate,
        newStartDate: newStartDate,
        newEndDate: newEndDate,
      })
    }
  }, [setPeriod, startDate, endDate, analytics, setUseLatestDate, intradayDate])

  const customizeOptions = useCallback((options) => [
    { value: SAME_DAY, label: "Same Day" },
    ...options,
  ], [])

  const handleDateChangeWithTradesCheck = useCallback((handleDateAction) => {
    if (trades?.length > 0) {
      dispatch(DialogCreators.open({
        title: "Confirm Date Change & Clear All Trades",
        contentText: "Changing the date will reset your trades. Would you still like to proceed with the date change?",
        buttons: [{
          text: "Confirm",
          type: "primary",
          action: () => {
            handleDateAction()
            resetTrades()
            analytics.track(DASHBOARD_EVENTS.TRADES_CLEARED)
          },
        }],
      }))
    } else {
      handleDateAction()
    }
  }, [trades, dispatch, resetTrades, analytics])

  const handleModelChange = useCallback((newModelId) => {
    if (trades?.length > 0) {
      dispatch(DialogCreators.open({
        title: "Confirm Model Change & Clear All Trades",
        contentText: "Changing the model will reset your trades. Would you still like to proceed with the model change?",
        buttons: [{
          text: "Confirm",
          type: "primary",
          action: () => {
            setModelId(newModelId)
            resetTrades()
            analytics.track(DASHBOARD_EVENTS.TRADES_CLEARED)
          },
        }],
      }))
    } else {
      setModelId(newModelId)
    }
  }, [trades, dispatch, setModelId, resetTrades, analytics])

  const onDownloadData = useCallback(() => ({ data: reduceDownloadData(), fileName: getDownloadDataFileName(instrumentMetadata, true) }), [reduceDownloadData, instrumentMetadata])

  const [isDownloadAllowedStatus, isDownloadAllowed] = useIsConstituentsDownloadAllowed({ modelId, positionSets: getIndexPositionSets([instrumentMetadata, comparisonInstrumentMetadata]) })

  const hasOptimizationComparison = useMemo(() => {
    return !isNil(comparison) && comparison.type === undefined && isOptimizeExperimentsEnabled
  }, [comparison, isOptimizeExperimentsEnabled])

  const { showRefreshButton, showWarning, handleIntradayRefresh, refreshButtonTooltip } = useIntradayRefresh(
    isIntraday,
    intradayDataAvailable,
    useLatestDate,
    lastIntradayBatch,
    intradayBatchMetadata,
    setIntradayBatchMetadata,
    setPeriod,
    period,
    adjustedStartDate,
    trades,
    resetTrades,
    onDownloadData,
    isPreferencesLoaded,
    isEndDateIntraday,
    lastIntradayBatchStatus,
    setUseLatestDate,
    setComparisonInstrument,
    setIsOptimizePanelExpanded,
    hasOptimizationComparison,
  )

  const handleSetPeriod = useCallback(change => {
    handleDateChangeWithTradesCheck(
      () => setPeriodWithChange(change),
    )
  }, [handleDateChangeWithTradesCheck, setPeriodWithChange])

  const handleResetEndDate = useCallback(() => {
    const resetDate = isIntraday ? intradayDate : latestDate

    setUseLatestDate(true)
    handleDateChangeWithTradesCheck(
      () => {
        setPeriod({
          period,
          endDate: resetDate,
          startDate: period === CUSTOM_PERIOD ? startDate : adjustedStartDate,
        })
        analytics.track(DASHBOARD_EVENTS.END_DATE_RESET_TO_LATEST, { oldEndDate: endDate, newEndDate: resetDate })
      },
    )
  }, [isIntraday, intradayDataAvailable, intradayDate, latestDate, setUseLatestDate, handleDateChangeWithTradesCheck, setPeriod, period, startDate, adjustedStartDate, analytics, endDate])

  const tooltipContent = <div>
    <div>Selected period: {period}</div>
    <br />
    <div><b>As of</b> date applies to point in time metrics; <b>Start</b> date used for performance, comparisons, & trends.</div>
  </div>

  return <TradeContext.Provider value={tradeContextValue}>
    <div className="modules_dashboard-root">
      <DashboardTopBar
        context={DashboardContext}
        instrumentMetadata={instrumentMetadata}
        comparisonInstrumentMetadata={comparisonInstrumentMetadata}
        baseInstrumentMetadata={baseInstrumentMetadata}
        adjustedStartDate={adjustedStartDate}
        onBaseSelected={(base, isDefault) => analytics.track(DASHBOARD_EVENTS.BASE_SELECTION, { base, isDefault })}
        onModelChange={handleModelChange}
      />
      <Paper square className="secondary-selectors">
        <div className="left-selectors">
          <CompareDateSelector {...{
            period: period,
            earliestDate: earliestDate,
            latestDate: latestDate,
            startDate: adjustedStartDate,
            endDate: endDate,
            onChange: handleSetPeriod,
            datesAdjusted,
            unmodeledDates,
            handleResetOption: handleResetEndDate,
            customizeOptions,
            isIntraday,
            intradayDataAvailable,
            isEndDateIntraday,
            intradayAsOf: currentAsOf,
            intradayBatchStatus: lastIntradayBatchStatus,
            skipAutoAdjustment: true, // Prevent double adjustment since dashboard already computed adjustedStartDate
            datesWithLabels: true,
            labels: PERIOD_SELECTOR_LABELS,
            tooltipContent,
          }}
          />
          {isIntradayInstrument && (
            <IntradayDataInfo
              isIntraday={isIntraday}
              isEndDateIntraday={isEndDateIntraday}
              currentAsOf={currentAsOf}
              showWarning={showWarning}
              showRefreshButton={showRefreshButton}
              onRefresh={handleIntradayRefresh}
              refreshButtonTooltip={refreshButtonTooltip}
              isLoadingDates={isLoadingDates}
              useLatestDate={useLatestDate}
            />
          )}
          {isIntradayAnalyticsEnabled && !isIntradayInstrument && latestPriceTimestamp && (
            <LatestPriceDisplay timestamp={latestPriceTimestamp} />
          )}
          {!isEmpty(modeSelectorOptions) && <>
            <Divider orientation="vertical" flexItem />
            <ListItemDropDown
              disableSubHeader
              classes={{
                toggle: "mode-selector",
              }}
              label="Showing"
              value={selectedMode}
              setValue={setSelectedMode}
              options={modeSelectorOptions}
              alignMenu="left"
              disablePortal
            />
          </>}
          <Divider orientation="vertical" flexItem />
          <TradeManager context={TradeContext} instrumentsWithMetadata={instrumentsWithMetadata} />
          <OPTooltip title="Reset Layout">
            <IconButton
              sx={{ marginLeft: -1 }}
              onClick={() => {
                const actions = layoutActions ?? dockviewLayoutRef.current
                const api = actions?.getApi?.()
                if (api) {
                  actions.resetLayout(api)
                }
              }}
              size="large"
            >
              <ResetTvOutlined />
            </IconButton>
          </OPTooltip>
        </div>
        <div className="right-selectors">
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Divider orientation="vertical" flexItem />
            <DashboardDownload context={DashboardContext} id="dashboard-download-button" instrumentMetadata={instrumentMetadata} isIntraday={isIntraday} disabled={isDownloadAllowedStatus !== OK || !isDownloadAllowed} reduceDownloadData={reduceDownloadData} />
            <OPTooltip title={filterSelectorStatus === ERROR ? "Error loading filters. Applying filters is disabled." : "Filter positions"}>
              <span>
                <IconButton
                  onClick={handleFiltersToggle}
                  size="large"
                  color={filterButtonAnchor ? "primary" : undefined}
                  disabled={filterSelectorStatus !== OK}
                  data-test-id="filters-toggle"
                >
                  <Badge overlap="rectangular" color="primary" invisible={!isFiltersBadgeVisible} badgeContent={filtersCount}>
                    <FilterList />
                  </Badge>
                </IconButton>
              </span>
            </OPTooltip>
          </div>
        </div>
      </Paper>
      <FilterSelector context={DashboardContext} setFilterSelectorStatus={setFilterSelectorStatus} hasMultipleInstruments={hasMultipleInstruments} anchor={filterButtonAnchor} toggleFilters={handleFiltersToggle} />
      {isAiTermsAccepted && !isAiChatFeatureEnabled && <QuickAnswersWrapper context={TradeContext} modelMetadata={modelMetadata} instrumentsWithMetadata={instrumentsWithMetadata} />}
      <div className={classNames("dashboard-content", { "optimizer-open": isOptimizeExperimentsEnabled && isOptimizePanelExpanded })}>
        {isOptimizeExperimentsEnabled && (
          <OptimizeExperimentsPanel
            isOptimizedExperimentsEnabled={isOptimizeExperimentsEnabled}
            context={DashboardContext}
            modelMetadata={modelMetadata}
          />
        )}
        <ModulesProvider>
          <ModulesEventListeners />
          <div className="dashboard-main-content">
            <DashboardModulesRenderer
              TradeContext={TradeContext}
              instrumentsWithMetadata={instrumentsWithMetadata}
              hasMultipleInstruments={hasMultipleInstruments}
              contentSets={contentSets}
              modelMetadata={modelMetadata}
              dockviewLayoutRef={dockviewLayoutRef}
              onLayoutReady={setLayoutActions}
            />
          </div>
        </ModulesProvider>
      </div>
    </div>
  </TradeContext.Provider>
}

function ModulesEventListeners() {
  usePinArtifactHandler()
  useRemoveArtifactHandler()
  return null
}

Dashboard.propTypes = {
  modelMetadata: MODEL_METADATA_SHAPE.isRequired,
  context: PropTypes.object.isRequired,
  instrumentMetadata: PropTypes.objectOf(INSTRUMENT_METADATA_SHAPE).isRequired,
  comparisonInstrumentMetadata: PropTypes.objectOf(INSTRUMENT_METADATA_SHAPE),
  baseInstrumentMetadata: PropTypes.objectOf(INSTRUMENT_METADATA_SHAPE),
  instrumentsWithMetadata: PropTypes.objectOf(INSTRUMENT_METADATA_SHAPE).isRequired,
  contentSets: PropTypes.arrayOf(CONTENT_SET_METADATA).isRequired,
  hasMultipleInstruments: PropTypes.bool.isRequired,
  lastIntradayBatch: PropTypes.object.isRequired,
  lastIntradayBatchStatus: PropTypes.oneOf(DATA_STATUSES).isRequired,
  TradeContext: PropTypes.object.isRequired,
  tradeContextValue: PropTypes.object.isRequired,
}
